import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  input,
  numberAttribute,
  signal,
} from '@angular/core';

import type { BackToTopViewport } from './index';

@Component({
  selector: 'app-back-to-top',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      class="back-to-top"
      type="button"
      [class.back-to-top--visible]="visible()"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-hidden]="visible() ? null : 'true'"
      [tabIndex]="visible() ? 0 : -1"
      (click)="scrollToTop()"
    >
      <span class="back-to-top__arrow" aria-hidden="true"></span>
    </button>
  `,
  styles: `
    :host {
      display: contents;

      --back-to-top-right: 1.5rem;
      --back-to-top-bottom: 1.5rem;
      --back-to-top-size: 3rem;
      --back-to-top-z-index: 1000;
    }

    .back-to-top {
      position: fixed;
      z-index: var(--back-to-top-z-index);

      right: var(--back-to-top-right);
      bottom: calc(var(--back-to-top-bottom) + env(safe-area-inset-bottom, 0px));

      display: grid;
      place-items: center;

      width: var(--back-to-top-size);
      height: var(--back-to-top-size);
      padding: 0;

      border: 1px solid color-mix(in srgb, currentColor 14%, transparent);
      border-radius: 50%;

      color: var(--text-color, #111827);

      background: color-mix(in srgb, var(--surface-card, #fff) 88%, transparent);

      box-shadow:
        0 4px 12px rgb(0 0 0 / 8%),
        0 12px 32px rgb(0 0 0 / 12%);

      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);

      cursor: pointer;

      opacity: 0;
      visibility: hidden;
      pointer-events: none;

      transform: translateY(0.75rem) scale(0.92);

      transition:
        opacity 160ms ease,
        transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
        visibility 160ms;
    }

    .back-to-top--visible {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;

      transform: translateY(0) scale(1);
    }

    .back-to-top--visible:hover {
      transform: translateY(-2px) scale(1.03);
    }

    .back-to-top--visible:active {
      transform: translateY(0) scale(0.96);
    }

    .back-to-top:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 3px;
    }

    .back-to-top__arrow {
      width: 0.7rem;
      height: 0.7rem;

      border-top: 2px solid currentColor;
      border-left: 2px solid currentColor;

      transform: translateY(2px) rotate(45deg);
    }

    @media (max-width: 768px) {
      .back-to-top {
        width: min(var(--back-to-top-size), 2.75rem);
        height: min(var(--back-to-top-size), 2.75rem);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .back-to-top {
        transition: none;
      }
    }
  `,
})
export class BackToTopComponent {
  readonly viewport = input<BackToTopViewport | null>(null);
  readonly threshold = input(320, {
    transform: numberAttribute,
  });

  readonly duration = input(700, {
    transform: numberAttribute,
  });

  readonly ariaLabel = input('Вернуться наверх');

  readonly visible = signal(false);
  private readonly ready = signal(false);

  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  private scrollContainer: HTMLElement | null = null;
  private animationFrame: number | null = null;
  private visibilityFrame: number | null = null;

  constructor() {
    afterNextRender(() => {
      this.ready.set(true);
    });

    effect((onCleanup) => {
      if (!this.ready()) {
        return;
      }

      const viewport = this.viewport();

      this.cancelAnimation();

      if (viewport) {
        this.scrollContainer = null;

        const stopScrollListener = viewport.listen(this.handleViewportScroll);
        const stopInteractionListener = viewport.listenUserInteraction?.(this.handleUserInteraction);

        this.updateVisibility();

        onCleanup(() => {
          stopScrollListener();
          stopInteractionListener?.();
        });

        return;
      }

      this.bindDocumentViewport();

      onCleanup(() => {
        this.unbindDocumentViewport();
      });
    });

    this.destroyRef.onDestroy(() => {
      this.cancelAnimation();
      this.cancelVisibilityFrame();
    });
  }

  scrollToTop(): void {
    const win = this.document.defaultView;

    if (!win) {
      return;
    }

    this.cancelAnimation();

    const start = this.getScrollTop();

    if (start <= 0) {
      return;
    }

    if (win.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setScrollTop(0);
      this.updateVisibility();

      return;
    }

    const startedAt = performance.now();
    const duration = Math.min(this.duration(), Math.max(280, start * 0.18));

    const animate = (now: number): void => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = this.easeOutCubic(progress);

      this.setScrollTop(start * (1 - eased));

      if (progress < 1) {
        this.animationFrame = win.requestAnimationFrame(animate);

        return;
      }

      this.animationFrame = null;

      this.setScrollTop(0);
      this.updateVisibility();
    };

    this.animationFrame = win.requestAnimationFrame(animate);
  }

  private readonly handleViewportScroll = (): void => {
    this.scheduleVisibilityUpdate();
  };

  private bindDocumentViewport(): void {
    this.resolveInitialScrollContainer();

    this.document.addEventListener('scroll', this.handleDocumentScroll, {
      capture: true,
      passive: true,
    });

    this.document.defaultView?.addEventListener('scroll', this.handleRootScroll, {
      passive: true,
    });

    this.document.addEventListener('wheel', this.handleUserInteraction, {
      capture: true,
      passive: true,
    });

    this.document.addEventListener('touchstart', this.handleUserInteraction, {
      capture: true,
      passive: true,
    });

    this.updateVisibility();
  }

  private unbindDocumentViewport(): void {
    this.document.removeEventListener('scroll', this.handleDocumentScroll, true);

    this.document.defaultView?.removeEventListener('scroll', this.handleRootScroll);

    this.document.removeEventListener('wheel', this.handleUserInteraction, true);

    this.document.removeEventListener('touchstart', this.handleUserInteraction, true);

    this.scrollContainer = null;
  }

  private readonly handleDocumentScroll = (event: Event): void => {
    const target = event.target;

    if (target instanceof HTMLElement && target.contains(this.host.nativeElement)) {
      this.scrollContainer = target;

      this.scheduleVisibilityUpdate();

      return;
    }

    if (target === this.document || target === this.document.documentElement || target === this.document.body) {
      this.scrollContainer = null;

      this.scheduleVisibilityUpdate();
    }
  };

  private readonly handleRootScroll = (): void => {
    const rootScrollTop = this.document.scrollingElement?.scrollTop ?? 0;

    if (!this.scrollContainer || rootScrollTop > 0) {
      this.scrollContainer = null;
    }

    this.scheduleVisibilityUpdate();
  };

  private resolveInitialScrollContainer(): void {
    let parent = this.host.nativeElement.parentElement;

    while (parent) {
      if (parent.scrollTop > 0) {
        this.scrollContainer = parent;

        return;
      }

      parent = parent.parentElement;
    }

    this.scrollContainer = null;
  }

  private readonly handleUserInteraction = (): void => {
    this.cancelAnimation();
  };

  private getScrollTop(): number {
    const viewport = this.viewport();

    if (viewport) {
      return viewport.getScrollTop();
    }

    if (this.scrollContainer) {
      return this.scrollContainer.scrollTop;
    }

    return this.document.scrollingElement?.scrollTop ?? this.document.defaultView?.scrollY ?? 0;
  }

  private setScrollTop(value: number): void {
    const viewport = this.viewport();

    if (viewport) {
      viewport.setScrollTop(value);

      return;
    }

    if (this.scrollContainer) {
      this.scrollContainer.scrollTop = value;

      return;
    }

    const scrollingElement = this.document.scrollingElement;

    if (scrollingElement) {
      scrollingElement.scrollTop = value;

      return;
    }

    this.document.defaultView?.scrollTo(0, value);
  }

  private scheduleVisibilityUpdate(): void {
    const win = this.document.defaultView;

    if (!win || this.visibilityFrame !== null) {
      return;
    }

    this.visibilityFrame = win.requestAnimationFrame(() => {
      this.visibilityFrame = null;

      this.updateVisibility();
    });
  }

  private updateVisibility(): void {
    this.visible.set(this.getScrollTop() >= this.threshold());
  }

  private cancelVisibilityFrame(): void {
    if (this.visibilityFrame === null) {
      return;
    }

    this.document.defaultView?.cancelAnimationFrame(this.visibilityFrame);
    this.visibilityFrame = null;
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  private cancelAnimation(): void {
    if (this.animationFrame === null) {
      return;
    }

    this.document.defaultView?.cancelAnimationFrame(this.animationFrame);

    this.animationFrame = null;
  }
}
